import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Typography, List, Avatar, Tag, Button, Space, message, Spin } from 'antd';
import {
    UserOutlined,
    ShoppingCartOutlined,
    InboxOutlined,
    PlusOutlined,
    FileTextOutlined,
    LineChartOutlined,
    FileSearchOutlined,
    RocketOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dashboardService from '../services/dashboardService';

const { Title, Text } = Typography;

const ShortcutItem = ({ title, icon, link, color }) => (
    <Col xs={12} sm={8}>
        <Link to={link}>
            <div
                className="shortcut-item"
                style={{
                    padding: '24px 12px',
                    textAlign: 'center',
                    borderRadius: 16,
                    background: '#ffffff',
                    border: '1px solid #f0f0f0',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.08)';
                    e.currentTarget.style.borderColor = color;
                    e.currentTarget.style.transform = 'translateY(-4px)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.02)';
                    e.currentTarget.style.borderColor = '#f0f0f0';
                    e.currentTarget.style.transform = 'translateY(0)';
                }}
            >
                <div style={{
                    width: 52,
                    height: 52,
                    borderRadius: 14,
                    background: color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 12,
                    boxShadow: `0 4px 12px ${color}33`,
                    color: '#fff',
                    fontSize: 24
                }}>
                    {icon}
                </div>
                <Text strong style={{ fontSize: '14px', color: '#262626' }}>{title}</Text>
            </div>
        </Link>
    </Col>
);

const Dashboard = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({
        totalSales: 0,
        newOrdersCount: 0,
        pendingOrdersCount: 0
    });

    const user = JSON.parse(localStorage.getItem('user') || '{}');

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const result = await dashboardService.getDashboardData();
            if (result.success) {
                setData(result.data);
            }
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            message.error('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    const stats = [
        {
            title: 'Current FY Total Sales',
            value: data.totalSales,
            icon: <ShoppingCartOutlined />,
            color: '#1890ff',
            bg: '#e6f7ff',
            isCurrency: true
        },
        {
            title: 'New Orders (30 Days)',
            value: data.newOrdersCount,
            icon: <PlusOutlined />,
            color: '#52c41a',
            bg: '#f6ffed'
        },
        {
            title: 'Pending Orders',
            value: data.pendingOrdersCount,
            icon: <InboxOutlined />,
            color: '#fa8c16',
            bg: '#fff7e6'
        },
    ];

    const shortcutsData = [
        { title: 'New Customer', icon: <UserOutlined />, link: '/customers', color: '#1890ff' },
        { title: 'New Order', icon: <ShoppingCartOutlined />, link: '/orders', color: '#52c41a' },
        { title: 'New Lead', icon: <PlusOutlined />, link: '/leads', color: '#faad14' },
        { title: 'Generate Offer', icon: <FileTextOutlined />, link: '/quotations', color: '#722ed1' },
        { title: 'MSEDCL Docs', icon: <FileSearchOutlined />, link: '/msedcl', color: '#eb2f96' },
        { title: 'Order Report', icon: <LineChartOutlined />, link: '/reports/orders', color: '#fa541c' },
    ];



    return (
        <div style={{ padding: '4px' }}>
            <div style={{ marginBottom: 24 }}>
                <Title level={2} style={{ margin: 0 }}>Dashboard</Title>
            </div>

            <Spin spinning={loading}>
                <Row gutter={[20, 20]}>
                    {stats.map((item, index) => (
                        <Col xs={24} sm={12} lg={8} key={index}>
                            <Card
                                bordered={false}
                                style={{
                                    borderRadius: 16,
                                    background: item.bg,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                                    overflow: 'hidden',
                                    position: 'relative',
                                    height: '100%'
                                }}
                                bodyStyle={{ padding: '24px' }}
                            >
                                <div style={{ position: 'absolute', right: -10, top: -10, opacity: 0.1, fontSize: 64, color: item.color }}>
                                    {item.icon}
                                </div>
                                <div style={{ position: 'relative', zIndex: 1 }}>
                                    <Text type="secondary" style={{ fontSize: 13, textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5, opacity: 0.7 }}>
                                        {item.title}
                                    </Text>
                                    <div style={{ marginTop: 8, display: 'flex', alignItems: 'baseline', gap: 4 }}>
                                        {item.isCurrency && <span style={{ fontSize: 18, color: '#262626', fontWeight: 600 }}>₹</span>}
                                        <Text style={{ fontSize: 28, fontWeight: 700, color: '#262626' }}>
                                            {item.isCurrency ? item.value.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : item.value}
                                        </Text>
                                    </div>
                                    <div style={{ marginTop: 4 }}>
                                        <Tag color={item.color} style={{ borderRadius: 10, border: 'none', fontSize: 10, padding: '0 10px' }}>Live Data</Tag>
                                    </div>
                                </div>
                            </Card>
                        </Col>
                    ))}
                </Row>
            </Spin>

            <Row gutter={[24, 24]} style={{ marginTop: 32 }}>
                <Col span={24}>
                    <Card
                        bordered={false}
                        style={{ borderRadius: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
                        bodyStyle={{ padding: '24px' }}
                    >
                        <Space direction="vertical" size="large" style={{ width: '100%' }}>
                            <Row justify="space-between" align="middle">
                                <Col>
                                    <Space>
                                        <Avatar size="large" icon={<RocketOutlined />} style={{ backgroundColor: '#1890ff' }} />
                                        <div>
                                            <h3 style={{ margin: 0, fontWeight: 600 }}>Quick Shortcuts</h3>
                                        </div>
                                    </Space>
                                </Col>
                            </Row>

                            <Row gutter={[16, 16]}>
                                {shortcutsData.map((shortcut, idx) => (
                                    <ShortcutItem
                                        key={idx}
                                        title={shortcut.title}
                                        icon={shortcut.icon}
                                        link={shortcut.link}
                                        color={shortcut.color}
                                    />
                                ))}
                            </Row>
                        </Space>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default Dashboard;
